import { useEffect, useRef } from "react";
import { FormattedMessage as FM } from "react-intl";

import ButtonsGroup from "componentsv2/button/ButtonsGroup";
import HorizontalDivider from "componentsv2/widgets/HorizontalDivider";
import { XMarkIcon } from "components/Icons";
import { cn } from "tools/ui";

const BASE_STYLE =
  "w-screen md:w-2/3 z-40 shadow-lg bg-neutral-100 rounded-lg mx-auto my-auto ring-none backdrop:bg-stone-900/50";
const BASE_STYLE_HEADER =
  "flex justify-between sticky top-0 bg-neutral-100 z-10 pt-2 pb-2 shrink-0";
const dialogSize = "w-screen md:w-3/5";

function Dialog({
  openModal,
  handleClose,
  handleAccept,
  handleNot,
  children,
  title,
  buttons,
  size,
  styleChild,
  style,
  enableEnterKey = true,
  fixedHeight = false,
}) {
  const ref = useRef(null);
  const size_ = size ?? dialogSize;

  useEffect(() => {
    if (!openModal) return;
    if (openModal.value) {
      ref.current?.showModal();
    } else {
      if (enableEnterKey) {
        ref.current?.close();
      }
    }
  }, [openModal?.value]);

  function onNot() {
    if (handleNot) {
      handleNot();
    }
  }

  function onAccept() {
    if (handleAccept) {
      handleAccept();
    }
  }

  function onClose() {
    openModal.value = false;
    if (handleClose) {
      handleClose();
    }
  }

  return (
    <dialog
      id="dialog-modal"
      className={cn(BASE_STYLE, style, size_, "max-h-[90vh]")}
      ref={ref}
      onCancel={onClose}
      onKeyDown={
        enableEnterKey
          ? (e) => {
              if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
                e.preventDefault();
                e.stopPropagation();
              }
            }
          : undefined
      }
    >
      {fixedHeight ? (
        <div className="flex flex-col h-full px-3 md:px-5 overflow-hidden">
          <div id="dialog-header" className={BASE_STYLE_HEADER}>
            {title ? (
              <h1 className="text-neutral-800 text-3xl my-auto">
                <FM id={title} key={title} />
              </h1>
            ) : (
              <span />
            )}
            <button
              type="button"
              className="ml-auto hover:bg-neutral-200 rounded-full relative cursor-pointer active:scale-95"
              onClick={onClose}
            >
              <XMarkIcon className="fill-current text-neutral-500 hover:text-rose-500 active:text-blue-300 w-8" />
            </button>
          </div>

          <div
            id="children-dialog"
            className={cn(
              "flex-1 min-h-0 overflow-y-auto w-full scroll-style",
              styleChild,
            )}
          >
            {children}
          </div>

          {buttons && (
            <div
              id="dialog-buttons"
              className="shrink-0 flex flex-col pt-1 w-full bg-neutral-100"
            >
              <HorizontalDivider index="button-separator" />
              <ButtonsGroup
                buttons={buttons}
                handleClose={onClose}
                handleAccept={onAccept}
                handleNot={onNot}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col px-3 md:px-5">
          <div id="dialog-header" className={BASE_STYLE_HEADER}>
            {title ? (
              <h1 className="text-neutral-800 text-3xl my-auto">
                <FM id={title} key={title} />
              </h1>
            ) : (
              <span />
            )}
            <button
              type="button"
              className="ml-auto hover:bg-neutral-200 rounded-full relative cursor-pointer active:scale-95"
              onClick={onClose}
            >
              <XMarkIcon className="fill-current text-neutral-500 hover:text-rose-500 active:text-blue-300 w-8" />
            </button>
          </div>

          <div
            id="children-dialog"
            className={cn("w-full py-4 scroll-style", styleChild)}
          >
            {children}
          </div>

          {buttons && (
            <div
              id="dialog-buttons"
              className="flex flex-col pt-1 w-full bg-neutral-100"
            >
              <HorizontalDivider index="button-separator" />
              <ButtonsGroup
                buttons={buttons}
                handleClose={onClose}
                handleAccept={onAccept}
                handleNot={onNot}
              />
            </div>
          )}
        </div>
      )}
    </dialog>
  );
}

export default Dialog;
